import { getToken } from './authUtils';
import { refreshAccessToken } from './refreshAccessToken';

/**
 * Authenticated `fetch` with transparent 401 recovery.
 *
 * Attaches the current access token, and if the server answers 401 (the token
 * expired between the last proactive refresh and this call — e.g. the user
 * clicked the instant they returned to a long-idle tab), it refreshes the token
 * once and retries the request. If the refresh fails the session is genuinely
 * over, so it redirects to /login instead of letting the caller render a broken,
 * data-less screen ("undefined" patient, blank report, data-source error).
 *
 * Use for authenticated backend calls that load screen-critical data. It reads
 * the token at call time, so it composes with TokenRefreshManager's proactive
 * refresh — this only handles the residual race.
 */
export async function authFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const withAuth = (token: string | null): RequestInit => ({
    ...init,
    headers: {
      ...(init.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const res = await fetch(input, withAuth(getToken()));
  if (res.status !== 401) {
    return res;
  }

  // Expired/invalid access token → single refresh + retry.
  const newToken = await refreshAccessToken();
  if (!newToken) {
    // Refresh token is dead too — the session is over. Send them to a clean
    // re-login rather than surfacing the 401 as a broken screen.
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    return res; // original 401; navigation is already in flight
  }
  return fetch(input, withAuth(newToken));
}
